/**
 * «Выбрано» мультипоиска. Список общий для экранов поиска и «Где есть всё» и
 * живёт в sessionStorage: переживает переходы туда-обратно и перезагрузку
 * вкладки, но не тянется в следующий визит — это выбор одного похода в аптеку.
 */
import { useCallback, useSyncExternalStore } from 'react'
import type { Product } from '../catalog/api'
import { addPicked, parsePicked, pick, removePicked, setPickedQty, type Picked } from './model'

export const SELECTION_STORAGE_KEY = 'dorixona.multisearch.v1'

const listeners = new Set<() => void>()
let cachedRaw: string | null | undefined
let cached: Picked[] = []

function read(): Picked[] {
  const raw = sessionStorage.getItem(SELECTION_STORAGE_KEY)
  // Тот же текст — тот же массив: иначе React перерисовывал бы без конца.
  if (raw !== cachedRaw) {
    cachedRaw = raw
    cached = parsePicked(raw)
  }
  return cached
}

function write(next: Picked[]) {
  sessionStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(next))
  listeners.forEach((listener) => {
    listener()
  })
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export interface Selection {
  items: Picked[]
  add: (product: Product) => void
  remove: (id: number) => void
  setQty: (id: number, qty: number) => void
}

export function useSelection(): Selection {
  const items = useSyncExternalStore(subscribe, read)
  const add = useCallback((product: Product) => {
    write(addPicked(read(), pick(product)))
  }, [])
  const remove = useCallback((id: number) => {
    write(removePicked(read(), id))
  }, [])
  const setQty = useCallback((id: number, qty: number) => {
    write(setPickedQty(read(), id, qty))
  }, [])
  return { items, add, remove, setQty }
}
